import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { 
  Typography, 
  Box, 
  Chip,
  Divider,
  TextField,
  InputAdornment
} from '@mui/material';
import { 
  FaArrowLeft, 
  FaUsers, 
  FaSearch,
  FaEye,
  FaEyeSlash,
  FaChartBar
} from 'react-icons/fa';
import { useAlert } from '../../contexts/AlertContext';
import { FEEDBACK_SESSION_VIEW_MODES } from '../../constants/feedbackSessionViewModes';
// ADR-014: closed-early discriminator for the report header + action locking
import {
  FEEDBACK_SESSION_STATUS_FILTERS,
  isSessionClosedWithIncomplete
} from '../../constants/feedbackSessionStatuses';
import { SessionStatusChip } from '../common/SessionStatusChip';
import {
  ASSIGNMENT_STATUSES,
  getAssignmentStatus,
  getAssignmentStatusText,
  getAssignmentStatusColor, 
  getAssignmentStatusTextColor, 
  getAssignmentStatusBorderColor,
  getReviewerStatus,
  getStatusText,
  getStatusColor,
  getStatusTextColor,
  getStatusBorderColor,
  getReviewTiming
} from '../../constants/assignmentStatuses';
import { REVIEW_TYPES, getReviewTypeText } from '../../constants/reviewTypes';
import { getTemplateTypeColor, getTemplateTypeText, getTemplateTypeConstant } from '../../constants/matrixTemplateTypes';
import { EmployeeAvatar } from '../common/EmployeeAvatar';
import LoadingSpinner from '../common/LoadingSpinner';
import EmptyState from '../common/EmptyState';
import AssignmentStatus from '../common/filters/AssignmentStatus';
import { getRelativeTime } from '../../utils/dateUtils';
import { 
  Container, 
  Header, 
  HeaderLeft, 
  BackButton, 
  Title, 
  Content, 
  MainSection,
  ControlsContainer,
  HeaderButton
} from '../common/styles';
import { COLORS } from '../../constants/radar';
import { feedbackApi } from '../../services/api';
import {
  AssigneeAdminActions,
  ReviewerAdminActions,
  AddAssigneeButton
} from './modifications/FeedbackSessionModifications';

const FeedbackSessionReport = ({ isIndividualAssignment = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId, assignmentId } = useParams();
  const { showAlert } = useAlert();
  const [loading, setLoading] = useState(true);
  const [sessionData, setSessionData] = useState(null);
  const [employeeAssignments, setEmployeeAssignments] = useState(new Map());
  const [viewMode, setViewMode] = useState(FEEDBACK_SESSION_VIEW_MODES.REPORT);
  const [searchTerm, setSearchTerm] = useState('');
  // Individual (assignee) status filter. Client-side only: the whole assignee
  // list is already loaded, so this needs no API change. Empty = show all.
  const [selectedAssignmentStatuses, setSelectedAssignmentStatuses] = useState([]);
  const [showControls, setShowControls] = useState(true);
  // ADR-003: bump to reload the session after an admin modification.
  const [reloadKey, setReloadKey] = useState(0);
  const reloadData = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    const fetchSessionData = async () => {
      try {
        setLoading(true);
        
        if (isIndividualAssignment) {
          // For individual assignments, get assignment ID from URL params
          const id = assignmentId;
          
          if (!id) {
            showAlert('Assignment ID not found', 'error');
            navigate('/individual-assignments');
            return;
          }
          
          // Fetch individual assignment data
          const assignmentData = await feedbackApi.getIndividualAssignmentById(id);
          
          // Transform individual assignment to session-like format
          const transformedSessionData = {
            id: assignmentData.id,
            sessionInfo: {
              id: assignmentData.id,
              name: `Individual Assignment - ${assignmentData.employee.fullName}`,
              month: new Date(assignmentData.assignedAt || Date.now()).getMonth() + 1,
              year: new Date(assignmentData.assignedAt || Date.now()).getFullYear(),
              deadline: assignmentData.reviewProcess?.deadline,
              allowEmployeeToAssignReviewers: assignmentData.reviewProcess?.allowEmployeeToAssignReviewers || false
            },
            businessUnit: {
              name: 'Individual Assignment'
            },
            // ADR-106 follow-up: an individual assignment is bound to a skills-matrix
            // session template through its review process. Surfacing it here lets
            // the modification dialogs (Change matrix / Add reviewer) scope their
            // options to that template, exactly as they do for a feedback session.
            sessionTemplate: assignmentData.reviewProcess?.sessionTemplate || null,
            reviewType: assignmentData.reviewers?.[0]?.reviewType || assignmentData.reviewProcess?.sessionTemplate?.reviewType,
            employeeAssignments: [{
              employeeId: assignmentData.employee.id,
              employee: {
                ...assignmentData.employee,
                orgGrades: assignmentData.employee.grade?.[0] || null,
                orgPositions: assignmentData.employee.position?.[0] || null
              },
              assignment: {
                id: assignmentData.id,
                assignedAt: assignmentData.assignedAt,
                completedAt: assignmentData.completedAt,
                feedbackAttachedAt: assignmentData.feedbackAttachedAt,
                reviewsCompletedAt: assignmentData.reviewsCompletedAt
              },
              matrix: assignmentData.skillsMatrix,
              reviewers: assignmentData.reviewers?.map(reviewer => ({
                reviewer: {
                  id: reviewer.employee.id,
                  fullName: reviewer.employee.fullName,
                  azureId: reviewer.employee.azureId,
                  assignment: {
                    id: reviewer.id,
                    assignedAt: reviewer.assignedAt,
                    completedAt: reviewer.completedAt,
                    startedAt: reviewer.startedAt,
                    order: reviewer.order || 1,
                    feedbackAttachedAt: reviewer.feedbackAttachedAt,
                    reviewsCompletedAt: reviewer.reviewsCompletedAt,
                    reviewType: reviewer.reviewType
                  }
                },
                template: reviewer.template,
                reviewType: reviewer.reviewType
              })) || []
            }]
          };
          
          setSessionData(transformedSessionData);
          
          // Convert single assignment to Map
          const assignmentsMap = new Map();
          assignmentsMap.set(assignmentData.employee.id, transformedSessionData.employeeAssignments[0]);
          setEmployeeAssignments(assignmentsMap);
        } else {
          // Original session logic
          // Get session ID from URL params or location state
          const id = sessionId || location.state?.sessionData?.id;
          
          if (!id) {
            showAlert('Session ID not found', 'error');
            navigate('/feedback-sessions');
            return;
          }
          
          const data = await feedbackApi.getSessionById(id);
          setSessionData(data);
          
          // Convert employee assignments array to Map
          const assignmentsMap = new Map();
          
          data.employeeAssignments.forEach(assignment => {
            assignmentsMap.set(assignment.employeeId, assignment);
          });
          
          setEmployeeAssignments(assignmentsMap);
        }
        
        // Set view mode from location state if provided
        if (location.state?.viewMode) {
          setViewMode(location.state.viewMode);
        }
      } catch (error) {
        showAlert(error.message || 'Failed to fetch session details', 'error', error);
        if (isIndividualAssignment) {
          navigate('/individual-assignments');
        } else {
          navigate('/feedback-sessions');
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchSessionData();
  }, [sessionId, assignmentId, isIndividualAssignment, navigate, showAlert, reloadKey]);

  // Callers that navigate here from somewhere other than the sessions list can
  // say where Back should go, using the app's existing returnTo/returnState
  // convention (see CVRepository, EmployeesRepository). Without it this page
  // always returned to /feedback-sessions, so arriving from a report — e.g.
  // the Assignment Completion BU detail page (ADR-094) — was a one-way trip
  // that silently discarded the caller's filters.
  //
  // Guarded on returnTo, so every existing caller keeps the list behaviour
  // below, scroll restoration included.
  const handleBack = () => {
    const { returnTo, returnState, scrollState } = location.state || {};

    if (returnTo) {
      navigate(returnTo, { state: returnState });
      return;
    }

    const returnPath = isIndividualAssignment ? '/individual-assignments' : '/feedback-sessions';
    navigate(returnPath, {
      state: {
        fromDetail: true,
        scrollState,
        needsRestore: true,
        hideContentUntilScrolled: true,
        timestamp: Date.now()
      }
    });
  };

  // "Back to <where you came from>" — a Back button that names the list while
  // returning to a report would be a small lie about where the click leads.
  const backLabel = location.state?.returnLabel
    || (isIndividualAssignment ? 'Individual Assignments' : 'Feedback Sessions');

  const handleAssignmentStatusToggle = (status) => {
    setSelectedAssignmentStatuses((current) => (
      current.includes(status)
        ? current.filter((selected) => selected !== status)
        : [...current, status]
    ));
  };

  /**
   * The shape `getAssignmentStatus` expects for an assignee row. The status
   * chip and the status filter both go through this, so a row can never be
   * filtered out under a status different from the one it displays.
   */
  const buildAssigneeStatusSource = (employeeId) => {
    const entry = employeeAssignments.get(employeeId);
    if (!entry) {
      return null;
    }
    return {
      ...entry.assignment,
      reviewers: entry.reviewers?.map(reviewer => reviewer?.reviewer?.assignment) || []
    };
  };

  const renderEmployeeCard = (employeeId) => {
    const assignment = employeeAssignments.get(employeeId);
    if (!assignment) return null;

    const employee = assignment.employee;
    if (!employee) return null;

    const employeeAssignmentObj = buildAssigneeStatusSource(employeeId);

    // Get the assignment ID from the assignment object
    const assignmentId = assignment.assignment?.id;

    return (
      <Box key={employeeId} sx={{ display: 'flex', mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            p: 1,
            backgroundColor: '#FFFFFF',
            borderRadius: 1,
            border: '1px solid #E2E8F0',
            width: '280px',
            height: '90px',
            position: 'relative',
          }}
        >
          <EmployeeAvatar
            employee={employee}
            size={28}
            sx={{ mr: 1 }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  color: '#334155',
                  fontWeight: 'normal',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px',
                  fontSize: '13px',
                  lineHeight: 1.2,
                  mb: 0.25
                }}
              >
                {employee.fullName || 'Unknown Individual'}
              </Typography>
              <Chip
                label={getAssignmentStatusText(employeeAssignmentObj)}
                size="small"
                sx={{
                  height: '18px',
                  fontSize: '10px',
                  backgroundColor: getAssignmentStatusColor(employeeAssignmentObj),
                  color: getAssignmentStatusTextColor(employeeAssignmentObj),
                  borderColor: getAssignmentStatusBorderColor(employeeAssignmentObj),
                  borderWidth: 1,
                  fontWeight: 500,
                  '& .MuiChip-label': {
                    px: 1
                  }
                }}
              />
              <AssigneeAdminActions
                sessionId={sessionId || sessionData?.id}
                assignmentId={assignmentId}
                assigneeName={employee.fullName}
                reviewType={sessionData?.reviewType}
                assignment={assignment}
                assignmentStatus={getAssignmentStatus(employeeAssignmentObj)}
                businessUnitId={sessionData?.businessUnit?.id}
                sessionTemplateId={sessionData?.sessionTemplate?.id}
                isPendingActions={!!employeeAssignmentObj.reviewsCompletedAt && !employeeAssignmentObj.feedbackAttachedAt}
                isIndividualAssignment={isIndividualAssignment}
                sessionLocked={!!sessionData?.isLocked}
                onChanged={reloadData}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: '#64748B',
                fontStyle: 'italic',
                display: 'flex',
                alignItems: 'center',
                gap: '2px',
                fontSize: '10px',
                lineHeight: 1.2,
                mb: 0.25
              }}
            >
              {employee.position?.label}
              {employee.grade && (
                <>
                  <Box component="span" sx={{ mx: 0.25 }}>•</Box>
                  {employee.grade.label}
                </>
              )}
            </Typography>
            {assignment.matrix && (
              <Typography 
                variant="caption" 
                sx={{ 
                      color: '#64748B',
                      fontSize: '10px',
                      display: 'block',
                      mt: 1
                }}
              >
                Assigned to {assignment.matrix.name}
              </Typography>
            )}
          </Box>
          
          {/* Report Icon */}
          <Box
            sx={{
              position: 'absolute',
              bottom: '8px',
              right: '8px',
              cursor: 'pointer',
              backgroundColor: '#F0F7FF',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #BFDBFE',
              '&:hover': {
                backgroundColor: '#DBEAFE',
              }
            }}
            onClick={() => navigate(`/feedback-sessions/assignment/${assignmentId}`, {
              state: { 
                returnContext: isIndividualAssignment ? 'individual-assignment' : 'feedback-session'
              }
            })}
          >
            <FaChartBar size={12} color="#3B82F6" />
          </Box>
        </Box>
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 1, 
          ml: 1,
          alignContent: 'flex-start',
          flex: 1
        }}>
          {assignment.reviewers && Array.isArray(assignment.reviewers) && assignment.reviewers.length > 0 && 
            [...assignment.reviewers]
            // Sort reviewers by order for Sequential reviews, keeping original order for others.
            // reviewType/order live at the top level for the individual-assignment shape, but are
            // nested under reviewer.assignment for the session response — read both.
            .sort((a, b) => {
              const aType = a?.reviewType ?? a?.reviewer?.assignment?.reviewType;
              const bType = b?.reviewType ?? b?.reviewer?.assignment?.reviewType;
              const aOrder = a?.order ?? a?.reviewer?.assignment?.order ?? 0;
              const bOrder = b?.order ?? b?.reviewer?.assignment?.order ?? 0;
              // If both have reviewType and it's SEQUENTIAL, sort by order
              if (aType === REVIEW_TYPES.SEQUENTIAL && bType === REVIEW_TYPES.SEQUENTIAL) {
                return aOrder - bOrder;
              }
              // If only a is SEQUENTIAL, it comes first
              if (aType === REVIEW_TYPES.SEQUENTIAL) return -1;
              // If only b is SEQUENTIAL, it comes first
              if (bType === REVIEW_TYPES.SEQUENTIAL) return 1;
              // Otherwise keep original order
              return 0;
            })
            .map((reviewerAssignment, index) => {
              if (!reviewerAssignment || !reviewerAssignment.reviewer) return null;

              // reviewType/order are top-level for the individual-assignment shape but
              // nested under reviewer.assignment for the session response.
              const reviewerReviewType = reviewerAssignment.reviewType ?? reviewerAssignment.reviewer?.assignment?.reviewType;
              const reviewerOrder = reviewerAssignment.order ?? reviewerAssignment.reviewer?.assignment?.order;

              return (
                <Box
                  key={`${reviewerAssignment.reviewer.id || index}-${index}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    p: 1,
                    backgroundColor: '#F8FAFC',
                    borderRadius: 1,
                    border: '1px solid #E2E8F0',
                    width: '280px',
                    height: '90px',
                    position: 'relative',
                  }}
                >
                  <EmployeeAvatar
                    employee={reviewerAssignment.reviewer}
                    size={28}
                    sx={{ mr: 1 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          color: '#64748B',
                          fontWeight: 'normal',
                          fontSize: '13px',
                          lineHeight: 1.2,
                          mb: 0.25
                        }}
                      >
                        {reviewerAssignment.reviewer.fullName || 'Unknown Reviewer'}
                      </Typography>
                      <Chip
                        label={getStatusText(getReviewerStatus(reviewerAssignment.reviewer.assignment))}
                        size="small"
                        sx={{
                          height: '18px',
                          fontSize: '10px',
                          backgroundColor: getStatusColor(getReviewerStatus(reviewerAssignment.reviewer.assignment)),
                          color: getStatusTextColor(getReviewerStatus(reviewerAssignment.reviewer.assignment)),
                          borderColor: getStatusBorderColor(getReviewerStatus(reviewerAssignment.reviewer.assignment)),
                          borderWidth: 1,
                          fontWeight: 500,
                          '& .MuiChip-label': {
                            px: 1
                          }
                        }}
                      />
                      <ReviewerAdminActions
                        assignmentId={assignmentId}
                        reviewerId={reviewerAssignment.reviewer.assignment?.id}
                        reviewerName={reviewerAssignment.reviewer.fullName}
                        isSelfReviewer={reviewerAssignment.reviewer.id === employeeId}
                        reviewer={reviewerAssignment.reviewer.assignment}
                        assignmentStatus={getAssignmentStatus(employeeAssignmentObj)}
                        sessionLocked={!!sessionData?.isLocked}
                        siblingReviewers={assignment.reviewers
                          ?.map((ra) =>
                            ra?.reviewer?.assignment
                              ? {
                                  ...ra.reviewer.assignment,
                                  fullName: ra.reviewer.fullName,
                                  employeeId: ra.reviewer.id,
                                }
                              : null
                          )
                          .filter(Boolean)}
                        onChanged={reloadData}
                      />
                    </Box>
                    {reviewerAssignment.template && reviewerAssignment.template.type && (
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: getTemplateTypeColor(getTemplateTypeConstant(reviewerAssignment.template.type)),
                          fontSize: '11px',
                          lineHeight: 1.2,
                          mb: 0.25
                        }}
                      >
                        {getTemplateTypeText(getTemplateTypeConstant(reviewerAssignment.template.type))}
                      </Typography>
                    )}
                    
                    {getReviewTiming(reviewerAssignment.reviewer.assignment) && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: '#64748B',
                          fontStyle: 'italic',
                          fontSize: '10px',
                          display: 'block',
                          mb: 0.25
                        }}
                      >
                        {getReviewTiming(reviewerAssignment.reviewer.assignment)}
                      </Typography>
                    )}
                    
                    {reviewerReviewType && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#64748B',
                          fontSize: '10px',
                          display: 'block',
                          mt: 0.5
                        }}
                      >
                        {getReviewTypeText(reviewerReviewType)}
                        {reviewerOrder && ` • Order: ${reviewerOrder}`}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })
          }
        </Box>
      </Box>
    );
  };

  // Filter employee assignments by search term and/or individual status
  const filteredEmployeeAssignments = () => {
    if (!sessionData?.employeeAssignments || !Array.isArray(sessionData.employeeAssignments)) {
      return [];
    }

    const lowerSearchTerm = searchTerm.toLowerCase().trim();
    const hasSearchTerm = lowerSearchTerm.length > 0;
    const hasStatusFilter = selectedAssignmentStatuses.length > 0;

    if (!hasSearchTerm && !hasStatusFilter) {
      return sessionData.employeeAssignments;
    }

    return sessionData.employeeAssignments.filter(assignment => {
      if (!assignment || !assignment.employeeId) return false;
      
      // Get the employee data from the Map
      const employeeData = employeeAssignments.get(assignment.employeeId);
      if (!employeeData) return false;

      // Status filter — derived from the same object the chip renders from.
      if (hasStatusFilter) {
        const statusSource = buildAssigneeStatusSource(assignment.employeeId);
        if (!statusSource) return false;
        if (!selectedAssignmentStatuses.includes(getAssignmentStatus(statusSource))) {
          return false;
        }
      }

      if (!hasSearchTerm) return true;
      
      // Check if employee name matches
      const employeeNameMatch = employeeData.employee?.fullName?.toLowerCase().includes(lowerSearchTerm);
      
      // Check if any reviewer name matches
      const reviewerMatch = Array.isArray(employeeData.reviewers) && 
        employeeData.reviewers.some(reviewer => 
          reviewer?.reviewer?.fullName?.toLowerCase().includes(lowerSearchTerm)
        );
      
      // Check if position or grade matches
      const positionMatch = employeeData.employee?.position?.label?.toLowerCase().includes(lowerSearchTerm);
      const gradeMatch = employeeData.employee?.grade?.label?.toLowerCase().includes(lowerSearchTerm);
      
      // Check if matrix name matches
      const matrixMatch = employeeData.matrix?.name?.toLowerCase().includes(lowerSearchTerm);
      
      return employeeNameMatch || reviewerMatch || positionMatch || gradeMatch || matrixMatch;
    });
  };

  if (loading) {
    return <LoadingSpinner message="Loading feedback session report..." />;
  }

  if (!sessionData) {
    return (
      <Container>
        <Header>
          <HeaderLeft>
            <BackButton onClick={handleBack}>
              <FaArrowLeft /> Back to {backLabel}
            </BackButton>
            <Title>Session Data Not Found</Title>
          </HeaderLeft>
        </Header>
        <Content>
          <MainSection>
            <EmptyState
              title="Session Data Not Found"
              description="The requested feedback session data could not be found. Please return to the sessions list and try again."
            />
          </MainSection>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={handleBack}>
            <FaArrowLeft /> Back to {backLabel}
          </BackButton>
          <Title>
            {isIndividualAssignment 
              ? 'Individual Assignment Report' 
              : `Feedback Session Report | ${sessionData.sessionInfo.name} | ${sessionData.businessUnit.name} | ${sessionData.sessionInfo.month}/${sessionData.sessionInfo.year}`
            }
          </Title>
          {/* ADR-014 §10: the terminal chip itself says how the session ended;
              the reason and completed/total counts live in its tooltip. */}
          {isSessionClosedWithIncomplete(sessionData) && (
            <SessionStatusChip
              session={sessionData}
              value={FEEDBACK_SESSION_STATUS_FILTERS.CLOSED_WITH_INCOMPLETE}
              labelSuffix={sessionData.closureSummary
                ? `${sessionData.closureSummary.completedAssignments}/${sessionData.closureSummary.totalAssignments} completed`
                : null}
              sx={{ ml: 2 }}
            />
          )}
        </HeaderLeft>
        <HeaderButton
          onClick={() => setShowControls(!showControls)}
          sx={{
            color: '#475569',
            borderColor: '#E2E8F0',
            '&:hover': {
              borderColor: '#CBD5E1',
              backgroundColor: '#F8FAFC',
            }
          }}
        >
          {showControls ? <FaEyeSlash /> : <FaEye />}
          {showControls ? 'Hide Panels' : 'Show Panels'}
        </HeaderButton>
      </Header>

      <ControlsContainer $show={showControls} style={{ justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search by individual or reviewer name"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoComplete="off"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <FaSearch style={{ color: '#6B7280' }} />
                  </InputAdornment>
                ),
              }
            }}
            sx={{ width: '500px' }}
          />
          {/* The chips render from ASSIGNMENT_STATUSES, so they stay in step with
              the status shown on each assignee card. */}
          <AssignmentStatus
            selectedStatuses={selectedAssignmentStatuses}
            onStatusToggle={handleAssignmentStatusToggle}
          />
        </Box>
        {!isIndividualAssignment && (sessionId || sessionData?.id) && (
          <AddAssigneeButton
            sessionId={sessionId || sessionData?.id}
            sessionTemplateId={sessionData?.sessionTemplate?.id}
            sessionLocked={!!sessionData?.isLocked}
            onChanged={reloadData}
          />
        )}
      </ControlsContainer>

      <Content>
        <MainSection>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <FaUsers size={18} style={{ marginRight: '8px', color: COLORS.PRIMARY }} />
              <Typography variant="h8" sx={{ color: COLORS.PRIMARY }}>Individuals and Reviewers</Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            
            {!sessionData.employeeAssignments || !Array.isArray(sessionData.employeeAssignments) || sessionData.employeeAssignments.length === 0 ? (
              <EmptyState
                title="No individuals assigned to this session"
                description="This feedback session doesn't have any individual assignments yet."
              />
            ) : (
              filteredEmployeeAssignments().map(assignment => 
                assignment && assignment.employeeId ? renderEmployeeCard(assignment.employeeId) : null
              )
            )}
            
            {filteredEmployeeAssignments().length === 0 && sessionData.employeeAssignments.length > 0 && (
              <EmptyState
                title="No individuals or reviewers match your search criteria"
                description="Try adjusting your search terms or filters to find what you're looking for."
              />
            )}
          </Box>
        </MainSection>
      </Content>
    </Container>
  );
};

export default FeedbackSessionReport; 